/**
 * UI Core — Pure UI logic without map dependencies.
 * Works with MapLibre via window.map and window.* helper functions.
 */
(function() {
  'use strict';

  // ── Debug logger ───────────────────────────────────────────────
  function escapeHtml(text) {
    if (text == null) return '';
    return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function log(cat, msg, data) {
    if (window.console && console.log) console.log('[' + cat + ']', msg, data || '');
  }

  window.onerror = function(msg, url, line, col, error) {
    log('ERROR', msg, { url, line, col, stack: error?.stack });
    return false;
  };
  window.addEventListener('unhandledrejection', function(e) {
    log('ERROR', 'Unhandled promise rejection', { reason: e.reason });
  });

  log('INIT', 'UI Core started');

  // ── Service Worker handling ────────────────────────────────────
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', function() {
      navigator.serviceWorker.register('/sw.js').then(function(registration) {
        console.log('[SW] Registered:', registration.scope);
        var swUpdateInterval = setInterval(function() { registration.update(); }, 5 * 60 * 1000);
        window.addEventListener('beforeunload', function() { clearInterval(swUpdateInterval); });
        registration.addEventListener('updatefound', function() {
          var newWorker = registration.installing;
          newWorker.addEventListener('statechange', function() {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[SW] New version available, activating...');
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      }).catch(function(error) {
        console.log('[SW] Registration failed:', error);
      });
      navigator.serviceWorker.addEventListener('message', function(event) {
        if (event.data && event.data.type === 'SW_UPDATED') {
          console.log('[SW] Updated to new version, reloading...');
          window.location.reload();
        }
      });
    });
  }

  // ── Panel handling ─────────────────────────────────────────────
  let activeInterfaceId = null;
  let activeMenuItem = null;

  function positionSecondaryMenu(menu, trigger) {
    const menuBar = document.getElementById('menu-bar');
    const iconRect = trigger.getBoundingClientRect();
    const menuBarRect = menuBar.getBoundingClientRect();

    menu.style.left = (menuBarRect.right + 10) + 'px';
    menu.style.top = iconRect.top + 'px';
    menu.style.right = '';
    menu.style.bottom = '';
    menu.classList.remove('horizontal');
    menu.classList.add('visible');

    requestAnimationFrame(function() {
      const rect = menu.getBoundingClientRect();
      const pad = 10;
      if (rect.bottom > window.innerHeight - pad) {
        menu.style.top = Math.max(pad, window.innerHeight - pad - rect.height) + 'px';
      }
      if (rect.top < pad) {
        menu.style.top = pad + 'px';
      }
    });
  }

  function closeActiveInterface() {
    if (!activeInterfaceId) return;
    const el = document.getElementById(activeInterfaceId);
    if (el) {
      el.classList.remove('open');
      el.classList.remove('visible');
    }
    if (activeMenuItem) activeMenuItem.classList.remove('active');
    activeInterfaceId = null;
    activeMenuItem = null;
    const overlay = document.getElementById('panel-overlay');
    if (overlay) overlay.classList.remove('visible');
  }

  function openInterface(id, menuItem) {
    // «Мои объявления» — только администратор (класс ставит admin-menu.js по роли с сервера)
    if (id === 'my-ads-panel' && !document.documentElement.classList.contains('is-admin')) {
      return;
    }
    closeActiveInterface();
    const lotDetail = document.getElementById('lot-detail-panel');
    if (lotDetail && id !== 'lot-detail-panel') {
      lotDetail.classList.remove('open');
    }
    const el = document.getElementById(id);
    if (!el) return;
    if (el.classList.contains('side-panel')) {
      el.classList.add('open');
      const overlay = document.getElementById('panel-overlay');
      if (overlay && id !== 'lot-detail-panel') overlay.classList.add('visible');
    } else if (el.classList.contains('menu-bar-secondary')) {
      positionSecondaryMenu(el, menuItem);
    }
    if (id === 'favorites-panel' && typeof window.renderFavoritesPanel === 'function') {
      window.renderFavoritesPanel();
    }
    if (id === 'my-ads-panel' && typeof window.renderMyAdsPanel === 'function') {
      window.renderMyAdsPanel();
    }
    if (id === 'info-panel' && typeof window.loadStats === 'function') {
      window.loadStats();
    }
    if (menuItem) menuItem.classList.add('active');
    activeInterfaceId = id;
    activeMenuItem = menuItem || null;
  }

  window.loadStats = async function() {
    const grid = document.getElementById('stats-grid');
    if (!grid) return;
    // A4: мгновенный рендер из кеша, обновление в фоне; лоадер только при первом открытии
    if (!window.swrPeek('stats')) {
      grid.innerHTML = '<div class="skel-row"></div><div class="skel-row w60"></div><div class="skel-row w40"></div>';
    }
    const renderStats = function(data) {
      const renderRow = (label, value, sub) => `
        <div class="lot-premium-row">
          <span class="lot-premium-label">${escapeHtml(label)}</span>
          <span class="lot-premium-value">${escapeHtml(value)}</span>
          ${sub ? '<span class="lot-premium-label" style="font-size:11px;">' + escapeHtml(sub) + '</span>' : ''}
        </div>`;
      grid.innerHTML = `
        ${renderRow('Добавлено сегодня', data.today.added, data.today.lastAddedAt ? 'в ' + data.today.lastAddedAt : null)}
        <div class="lot-premium-row lot-premium-row--full" style="border-bottom: 1px solid #f4f6f8; margin-bottom: 8px; padding-bottom: 8px;"></div>
        ${renderRow('Всего лотов', data.today.totalVisible.toLocaleString('ru-RU'))}
        ${renderRow('На карте (с координатами)', data.today.withCoordinates.toLocaleString('ru-RU'))}
        ${renderRow('С границами участков', data.today.withBoundaries.toLocaleString('ru-RU'))}
        <div class="lot-premium-row lot-premium-row--full" style="border-bottom: 1px solid #f4f6f8; margin-bottom: 8px; padding-bottom: 8px;"></div>
        ${renderRow('Добавлено вчера', data.yesterday.added, data.yesterday.lastAddedAt ? 'в ' + data.yesterday.lastAddedAt : null)}
        <div class="lot-premium-row lot-premium-row--full" style="border-bottom: 1px solid #f4f6f8; margin-bottom: 8px; padding-bottom: 8px;"></div>
        ${renderRow('За неделю', data.week.added.toLocaleString('ru-RU'))}
        ${renderRow('За месяц', data.month.added.toLocaleString('ru-RU'))}
        ${renderRow('За год', data.year.added.toLocaleString('ru-RU'))}
      `;
    };
    try {
      const res = await window.swrFetch('stats', '/api/stats', {}, { maxAgeMs: 60000 });
      renderStats(res.data);
    } catch (e) {
      grid.innerHTML = '<div class="lot-premium-row lot-premium-row--full"><span class="lot-premium-label" style="color:#e74c3c;">Ошибка загрузки статистики</span></div>';
    }
  };

  window.closeActiveInterface = closeActiveInterface;
  window.openInterface = openInterface;
  window.closeInterfaceById = function(id) {
    if (activeInterfaceId === id) closeActiveInterface();
  };

  // ── Menu bar click handlers ────────────────────────────────────
  function initPanelHandlers() {
    document.querySelectorAll('.menu-item').forEach(function(item) {
      item.addEventListener('click', function(e) {
        e.stopPropagation();
        // Let real anchor links navigate normally.
        if (this.tagName === 'A' && this.href) {
          return;
        }
        // Resolve target first so we can close any open panel before auth checks.
        let targetId;
        if (this.dataset.panel === 'layers') {
          targetId = 'layers-secondary-menu';
        } else if (this.dataset.panel) {
          targetId = this.dataset.panel + '-panel';
        } else if (this.dataset.toggle) {
          targetId = this.dataset.toggle + '-secondary-menu';
        } else {
          targetId = null;
        }
        if (!targetId) {
          // Profile button has no panel/toggle attributes
          if (this.dataset.auth === 'profile') {
            if (activeInterfaceId === 'profile-panel' && activeMenuItem === this) {
              closeActiveInterface();
              return;
            }
            openInterface('profile-panel', this);
          } else {
            closeActiveInterface();
          }
          return;
        }
        if (activeInterfaceId === targetId && activeMenuItem === this) {
          closeActiveInterface();
          return;
        }
        // Auth-gated items: filters, favorites, my-ads, profile.
        // Close the currently open panel first, then prompt for login if needed.
        if (this.dataset.auth) {
          const auth = window.goszemlyaAuth;
          if (auth && typeof auth.requireAuth === 'function') {
            if (!auth.requireAuth()) {
              closeActiveInterface();
              return;
            }
          }
        }
        // On the listings page, open my-ads on the home page so the listings view closes.
        if (targetId === 'my-ads-panel' && window.location.pathname === '/obyavleniya') {
          window.location.href = '/?openMyAds=1';
          return;
        }
        openInterface(targetId, this);
      });
    });

    // Layer buttons require authentication for unauthorized users.
    // Uses capture phase to stop the event before per-button handlers switch the layer.
    const layersMenu = document.getElementById('layers-secondary-menu');
    if (layersMenu) {
      layersMenu.addEventListener('click', function(e) {
        const btn = e.target.closest('.menu-item-secondary');
        if (!btn) return;
        const auth = window.goszemlyaAuth;
        if (auth && typeof auth.requireAuth === 'function' && !auth.requireAuth()) {
          e.stopPropagation();
          e.preventDefault();
        }
      }, true);
    }

    // Submit Ad button
    const submitAdBtn = document.querySelector('.menu-item[data-action="submit-ad"]');
    if (submitAdBtn) {
      submitAdBtn.addEventListener('click', function() {
        closeActiveInterface();
        if (typeof window.openAdModal === 'function') {
          window.openAdModal();
        } else {
          alert('Модуль подачи объявлений загружается...');
        }
      });
    }

    // Close buttons on side-panels
    document.querySelectorAll('.close-btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        const panel = this.closest('.side-panel');
        if (!panel) return;
        panel.classList.remove('open');
        if (activeInterfaceId === panel.id) {
          if (activeMenuItem) activeMenuItem.classList.remove('active');
          activeInterfaceId = null;
          activeMenuItem = null;
        }
      });
    });

    // Click on empty map: close side panels on mobile only
    const map1 = document.getElementById('map1');
    if (map1) {
      map1.addEventListener('click', function(e) {
        if (window.innerWidth >= 769) return;
        if (activeInterfaceId && activeInterfaceId !== 'lot-detail-panel' && !activeInterfaceId.endsWith('-secondary-menu')) {
          closeActiveInterface();
        }
      });
    }
  }

  // ── Cadastre controls (UI only — delegated to maplibre-app.js setupUI) ─
  function initCadastreControls() {
    const cadastreOpacity = document.getElementById('cadastre-opacity');
    const opacityValue = document.getElementById('opacity-value');
    if (cadastreOpacity && opacityValue) {
      cadastreOpacity.addEventListener('input', function() {
        const opacity = this.value / 100;
        if (typeof window.setCadastreOpacity === 'function') {
          window.setCadastreOpacity(opacity);
        }
        opacityValue.textContent = this.value + '%';
      });
    }
  }

  // ── Window resize ──────────────────────────────────────────────
  window.addEventListener('resize', function() {
    if (activeInterfaceId && activeMenuItem) {
      const el = document.getElementById(activeInterfaceId);
      if (el && el.classList.contains('menu-bar-secondary')) {
        positionSecondaryMenu(el, activeMenuItem);
      }
    }
  });

  // ── State Management (LocalStorage) ────────────────────────────
  const STATES_KEY = 'webmap_saved_states';

  function showMessage(msg, type) {
    const statesMessage = document.getElementById('states-message');
    if (!statesMessage) return;
    statesMessage.textContent = msg;
    statesMessage.style.display = 'block';
    statesMessage.style.background = type === 'success' ? '#d4edda' : '#f8d7da';
    statesMessage.style.color = type === 'success' ? '#155724' : '#721c24';
    setTimeout(function() { statesMessage.style.display = 'none'; }, 3000);
  }

  function getSavedStates() {
    try { return JSON.parse(localStorage.getItem(STATES_KEY) || '[]'); } catch (e) { return []; }
  }

  function saveStatesList(states) {
    localStorage.setItem(STATES_KEY, JSON.stringify(states));
  }

  function saveMapState() {
    if (typeof window.getMapState !== 'function') {
      showMessage('Карта ещё не инициализирована', 'error');
      return;
    }
    const state = window.getMapState();
    state.fileName = 'state_' + new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    state.createdAt = new Date().toISOString();

    try {
      const states = getSavedStates();
      states.unshift(state);
      saveStatesList(states);
      showMessage('Сохранено: ' + state.fileName, 'success');
      log('STATE', 'Saved: ' + state.fileName);
      loadStatesListUI();
    } catch (error) {
      showMessage('Ошибка сохранения: ' + error.message, 'error');
      log('ERROR', 'Save failed: ' + error.message);
    }
  }

  function loadStatesListUI() {
    const statesList = document.getElementById('states-list');
    if (!statesList) return;
    try {
      const states = getSavedStates();
      if (states.length === 0) {
        statesList.innerHTML = '<div style="text-align: center; padding: 20px; color: #999;">Нет сохранённых состояний</div>';
        return;
      }
      statesList.innerHTML = states.map(function(state) {
        return `
          <div style="padding: 12px; margin: 8px 0; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #667eea;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
              <strong style="color: #1a1a2e;">${escapeHtml(state.fileName)}</strong>
              <span style="font-size: 11px; color: #888;">${state.createdAt ? state.createdAt.split('T')[0] : 'N/A'}</span>
            </div>
            <div style="font-size: 12px; color: #666; margin-bottom: 8px;">
              🗺️ ${state.layer || 'N/A'} | 🔍 ${state.zoom != null ? state.zoom.toFixed(1) : 'N/A'} | 📍 ${state.center ? state.center[1].toFixed(4) + ', ' + state.center[0].toFixed(4) : 'N/A'}
            </div>
            <div style="display: flex; gap: 8px;">
              <button onclick="loadState('${escapeHtml(state.fileName).replace(/'/g, "\\'")}')" style="flex: 1; padding: 6px; background: #28a745; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px;">
                📂 Загрузить
              </button>
              <button onclick="deleteState('${escapeHtml(state.fileName).replace(/'/g, "\\'")}')" style="flex: 1; padding: 6px; background: #dc3545; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px;">
                🗑️ Удалить
              </button>
            </div>
          </div>`;
      }).join('');
    } catch (error) {
      statesList.innerHTML = '<div style="text-align: center; padding: 20px; color: #dc3545;">Ошибка загрузки: ' + escapeHtml(error.message) + '</div>';
    }
  }

  window.loadState = function(fileName) {
    try {
      const states = getSavedStates();
      const state = states.find(function(s) { return s.fileName === fileName; });
      if (!state) {
        showMessage('Состояние не найдено', 'error');
        return;
      }
      if (typeof window.setMapState === 'function') {
        window.setMapState(state);
      }
      // Update layer switcher buttons
      if (state.layer) {
        document.querySelectorAll('#layers-secondary-menu .menu-item-secondary').forEach(function(btn) {
          const layerType = btn.dataset.layer;
          if (layerType === state.layer) btn.classList.add('active');
          else if (layerType !== 'cadastre' && !layerType.startsWith('nspd-')) btn.classList.remove('active');
        });
      }
      showMessage('Состояние загружено: ' + fileName, 'success');
      log('STATE', 'Loaded: ' + fileName);
    } catch (error) {
      showMessage('Ошибка загрузки: ' + error.message, 'error');
      log('ERROR', 'Load failed: ' + error.message);
    }
  };

  window.deleteState = function(fileName) {
    if (!confirm('Удалить состояние "' + fileName + '"?')) return;
    try {
      let states = getSavedStates();
      states = states.filter(function(s) { return s.fileName !== fileName; });
      saveStatesList(states);
      showMessage('Удалено: ' + fileName, 'success');
      log('STATE', 'Deleted: ' + fileName);
      loadStatesListUI();
    } catch (error) {
      showMessage('Ошибка удаления: ' + error.message, 'error');
      log('ERROR', 'Delete failed: ' + error.message);
    }
  };

  function initStateManagement() {
    const saveStateBtn = document.getElementById('save-state-btn');
    if (saveStateBtn) saveStateBtn.addEventListener('click', saveMapState);

    const refreshStatesBtn = document.getElementById('refresh-states-btn');
    if (refreshStatesBtn) refreshStatesBtn.addEventListener('click', loadStatesListUI);

    const statesMenuItem = document.querySelector('.menu-item[data-panel="states"]');
    if (statesMenuItem) {
      statesMenuItem.addEventListener('click', function() {
        setTimeout(loadStatesListUI, 300);
      });
    }
    loadStatesListUI();
  }

  // ── Global close / copy functions ──────────────────────────────
  window.closePanel = function(panelId) {
    const panel = document.getElementById(panelId);
    const wasOpen = !!(panel && panel.classList.contains('open'));
    if (panel) {
      panel.classList.remove('open');
      const panelId2 = panelId.replace('-panel', '');
      const menuItem = document.querySelector('.menu-item[data-panel="' + panelId2 + '"]');
      if (menuItem) menuItem.classList.remove('active');
    }
    // Guard от бесконечной рекурсии: hideLotPopup диспатчит 'lot:close',
    // слушатель которого снова вызывает closePanel. Диспатчим только если
    // панель реально была открыта (fix: "Maximum call stack size exceeded")
    if (panelId === 'lot-detail-panel' && wasOpen && typeof window.hideLotPopup === 'function') {
      window.hideLotPopup();
    }
    const overlay = document.getElementById('panel-overlay');
    if (overlay) overlay.classList.remove('visible');
  };

  window.copyCoords = function() {
    const items = document.querySelectorAll('#coords-list .coord-item');
    if (items.length === 0) return;
    const text = Array.from(items).map(function(item) { return item.textContent.replace(/^\d+:\s*/, ''); }).join('\n');
    navigator.clipboard.writeText(text).then(function() {
      const btn = document.querySelector('#coords-panel .copy-btn');
      if (!btn) return;
      const original = btn.textContent;
      btn.textContent = 'Скопировано!';
      btn.style.background = '#28a745';
      setTimeout(function() {
        btn.textContent = original;
        btn.style.background = '#667eea';
      }, 1500);
    });
  };

  // ── VRI data loader ────────────────────────────────────────────
  function initVRI() {
    if (typeof VRI_CLASSIFIER !== 'undefined') return;
    if (document.querySelector('script[src^="/js/vri-data.js"]')) return;
    const vriScript = document.createElement('script');
    vriScript.src = '/js/vri-data.js?v=2';
    document.body.appendChild(vriScript);
  }

  // ── Auto open lot from /lot/:id URL ────────────────────────────
  function initAutoOpenLot() {
    const autoLotId = window.AUTO_OPEN_LOT_ID;
    const autoLotKn = window.AUTO_OPEN_LOT_KN;
    if (!autoLotId && !autoLotKn) return;
    // C2: мгновенный триггер по событию карты; короткий поллинг остался страховкой
    let autoOpened = false;
    const targetId = autoLotId || autoLotKn;
    const tryOpen = function() {
      if (autoOpened || typeof window.focusLotOnMap !== 'function') return;
      autoOpened = true;
      console.log('[AUTO_OPEN] Focusing lot:', targetId);
      try { window.focusLotOnMap(targetId); } catch (e) {
        console.error('[AUTO_OPEN] focus failed', e);
        autoOpened = false;
      }
    };
    window.addEventListener('map:ready', function() { setTimeout(tryOpen, 0); });
    setTimeout(tryOpen, 8000);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() { tryOpen(); });
    } else {
      tryOpen();
    }
  }

  // ── Favorites panel renderer ─────────────────────────────────
  window.renderFavoritesPanel = function() {
    syncFavoritesWithBackend();
    const container = document.getElementById('favorites-content');
    if (!container) return;
    let data = {};
    try { data = JSON.parse(localStorage.getItem('webmap_favorites_data') || '{}'); } catch(e) {}
    const ids = Object.keys(data);
    if (ids.length === 0) {
      container.innerHTML = `
        <div class="lot-premium-section" style="text-align:center;padding:40px 20px;">
          <div style="font-size:48px;margin-bottom:16px;opacity:.5;">⭐</div>
          <p style="color:#999;">Нет избранных мест</p>
        </div>`;
      return;
    }
    function fmtPrice(n) {
      if (n == null || n === '') return 'Цена не указана';
      const num = Number(n);
      if (isNaN(num)) return String(n);
      if (num >= 1000000000) return (num / 1000000000).toFixed(num % 1000000000 === 0 ? 0 : 2).replace('.', ',') + ' млрд ₽';
      if (num >= 1000000) return (num / 1000000).toFixed(num % 1000000 === 0 ? 0 : 1).replace('.', ',') + ' млн ₽';
      return num.toLocaleString('ru-RU') + ' ₽';
    }
    function fmtArea(n) {
      if (n == null || n === '') return '';
      const num = Number(n);
      if (isNaN(num)) return '';
      return num.toLocaleString('ru-RU') + ' м²';
    }
    function getTxLabel(code, bfc) {
      const tx = String(code || '').toLowerCase();
      const bf = String(bfc || '').toUpperCase();
      if (bf === 'EA' && tx === 'rent') return 'Аукцион аренда';
      if (bf === 'EA' && tx === 'sale') return 'Аукцион продажа';
      if (tx === 'sale' && bf !== 'EA' && bf !== 'IPS') return 'Прямая продажа';
      if ((tx === 'rent' || tx === 'lease') && bf !== 'EA' && bf !== 'IPS') return 'Переуступка права аренды';
      if (bf === 'IPS') return 'БВП';
      return 'Продажа';
    }
    function esc(str) {
      if (!str) return '';
      return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
    const items = ids.map(function(id) { return data[id]; }).filter(Boolean);
    const needsRefresh = items.some(function(item) {
      return !Array.isArray(item.foto_urls_json) || item.foto_urls_json.length === 0;
    });
    if (needsRefresh && typeof window.refreshFavoritesData === 'function' && !window._refreshingFavorites) {
      window.refreshFavoritesData();
    }
    let html = '<div class="favorites-grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:14px;padding:14px;">';
    items.forEach(function(item) {
      const photo = (Array.isArray(item.foto_urls_json) && item.foto_urls_json.length > 0) ? ('/storage/' + esc(item.foto_urls_json[0]) + '?w=400') : '';
      const lotUrl = window.location.origin + '/lot/' + encodeURIComponent(item.cadastral_number || item.lot_id || '');
      const tx = getTxLabel(item.type_transaction, item.bidd_form_code);
      const lotIdJs = esc(String(item.lot_id).replace(/'/g, "\\'"));
      const area = fmtArea(item.area);
      html += `
        <div class="favorite-card" style="background:#fff;border-radius:14px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.06);border:1px solid #e9ecef;cursor:pointer;transition:transform .15s,box-shadow .15s;" onmouseenter="this.style.transform='translateY(-2px)';this.style.boxShadow='0 6px 16px rgba(0,0,0,.1)';" onmouseleave="this.style.transform='';this.style.boxShadow='';" onclick="window.focusLotOnMap('${lotIdJs}'); window.dispatchEvent(new CustomEvent('lot:select', { detail: { lotId: '${lotIdJs}', source: 'favorites' } }));">
          <div style="position:relative;height:150px;background:#f1f5f9;overflow:hidden;">
            ${photo ? '<img src="' + photo + '" style="width:100%;height:100%;object-fit:cover;" alt="" loading="lazy">' : '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:#94a3b8;font-size:40px;">🏞️</div>'}
            <button class="fav-remove-btn" data-lot-id="${esc(String(item.lot_id))}" title="Удалить из избранного" style="position:absolute;top:8px;right:8px;width:32px;height:32px;border:none;background:rgba(255,255,255,.9);border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#f59e0b;box-shadow:0 2px 6px rgba(0,0,0,.15);" onclick="event.stopPropagation();">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" fill="currentColor" style="width:16px;height:16px;"><path stroke-linecap="round" stroke-linejoin="round" d="M9.153 5.408C10.42 3.136 11.053 2 12 2s1.58 1.136 2.847 3.408l.328.588c.36.646.54.969.82 1.182s.63.292 1.33.45l.636.144c2.46.557 3.689.835 3.982 1.776.292.94-.546 1.921-2.223 3.882l-.434.507c-.476.557-.715.836-.822 1.18-.107.345-.071.717.001 1.46l.066.677c.253 2.617.38 3.925-.386 4.506s-1.918.051-4.22-1.009l-.597-.274c-.654-.302-.981-.452-1.328-.452s-.674.15-1.328.452l-.596.274c-2.303 1.06-3.455 1.59-4.22 1.01-.767-.582-.64-1.89-.387-4.507l.066-.676c.072-.744.108-1.116 0-1.46-.106-.345-.345-.624-.821-1.18l-.434-.508c-1.677-1.96-2.515-2.941-2.223-3.882S3.58 8.328 6.04 7.772l.636-.144c.699-.158 1.048-.237 1.329-.45s.46-.536.82-1.182z"/></svg>
            </button>
          </div>
          <div style="padding:12px;">
            <div style="font-size:17px;font-weight:800;color:#1a1a2e;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${fmtPrice(item.price_min)}</div>
            <div style="display:flex;gap:6px;align-items:center;margin-top:6px;flex-wrap:wrap;">
              <span style="display:inline-flex;align-items:center;padding:3px 8px;border-radius:5px;font-size:11px;font-weight:700;background:#eef2ff;color:#4f46e5;text-transform:uppercase;">${esc(tx)}</span>
              ${area ? '<span style="display:inline-flex;align-items:center;padding:3px 8px;border-radius:5px;font-size:11px;font-weight:500;background:#f1f5f9;color:#475569;">' + esc(area) + '</span>' : ''}
            </div>
            <div style="font-size:13px;color:#64748b;margin-top:8px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${esc(item.region_name || '')}</div>
            <div style="font-size:12px;color:#94a3b8;font-family:monospace;margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${esc(item.cadastral_number || item.lot_id || '')}</div>
            <div style="display:flex;gap:8px;margin-top:12px;">
              <button class="fav-copy-btn" title="Скопировать ссылку" style="flex:1;display:flex;align-items:center;justify-content:center;gap:6px;padding:8px 10px;border:1px solid #e2e8f0;border-radius:8px;background:#fff;color:#475569;font-size:12px;font-weight:600;cursor:pointer;" onclick="event.stopPropagation(); window.copyToClipboard('${esc(lotUrl)}', this);">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
                Ссылка
              </button>
            </div>
          </div>
        </div>`;
    });
    html += '</div>';
    container.innerHTML = html;
    container.querySelectorAll('.fav-remove-btn').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault(); e.stopPropagation();
        const lotId = btn.dataset.lotId;
        let d = {};
        try { d = JSON.parse(localStorage.getItem('webmap_favorites_data') || '{}'); } catch(e) {}
        delete d[lotId];
        localStorage.setItem('webmap_favorites_data', JSON.stringify(d));
        if (window.goszemlyaAnalytics && window.goszemlyaAnalytics.trackEvent) {
            window.goszemlyaAnalytics.trackEvent('favorite_remove', { lotId: String(lotId) });
        }
        window.renderFavoritesPanel();
      });
    });
  };

  window.syncFavoritesWithBackend = function() {
    if (!window.goszemlyaAuth || !window.goszemlyaAuth.isAuthenticated()) return;
    let data = {};
    try { data = JSON.parse(localStorage.getItem('webmap_favorites_data') || '{}'); } catch(e) { return; }
    const lotIds = Object.keys(data);
    if (lotIds.length === 0) return;
    apiFetch('/api/analytics/favorites/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lotIds })
    }).catch(() => {});
  }

  window.addEventListener('auth:change', function(event) {
    if (event.detail && event.detail.authenticated && typeof window.syncFavoritesWithBackend === 'function') {
      window.syncFavoritesWithBackend();
    }
  });

  // ── My Ads panel renderer ──────────────────────────────────────
  async function apiFetch(url, options) {
    options = options || {};
    options.headers = options.headers || {};
    const tgInitData = window.__TG_INIT_DATA__;
    const auth = window.goszemlyaAuth;
    if (tgInitData) {
      options.headers['Authorization'] = 'tma ' + tgInitData;
    } else if (auth && typeof auth.getToken === 'function') {
      const token = auth.getToken();
      if (token) options.headers['Authorization'] = 'Bearer ' + token;
    }
    if (!options.headers['Content-Type'] && options.body && typeof options.body === 'string') {
      options.headers['Content-Type'] = 'application/json';
    }
    return fetch(url, options);
  }

  const AD_TYPE_LABELS = {
    sale_ownership: 'Прямая продажа',
    sale_lease_right: 'Уступка права аренды',
    rent_own: 'Сдача в аренду',
    rent_sublet: 'Субаренда'
  };
  const AD_STATUS_LABELS = {
    active: 'Активно',
    pending: 'На модерации',
    rejected: 'Отклонено',
    archived: 'Архив',
    draft: 'Черновик',
    inactive: 'Не активно'
  };
  const AD_STATUS_CLASS = {
    active: 'active',
    pending: 'pending',
    rejected: 'rejected',
    archived: 'archived',
    draft: 'draft',
    inactive: 'inactive'
  };

  function formatArea(area) {
    if (!area && area !== 0) return '—';
    return Number(area).toLocaleString('ru-RU') + ' м²';
  }
  function formatPrice(price) {
    if (!price && price !== 0) return '—';
    return Number(price).toLocaleString('ru-RU') + ' ₽';
  }
  function getRegionNameForAd(code) {
    if (!code) return '—';
    if (typeof window.getRegionName === 'function') return window.getRegionName(code) || code;
    return code;
  }

  const DEFAULT_COLUMNS = ['num', 'ad_id', 'cadastral_number', 'ad_type', 'region', 'area', 'price', 'status', 'actions'];
  const COLUMN_TITLES = {
    num: '№',
    ad_id: 'ID',
    cadastral_number: 'Кадастровый номер',
    ad_type: 'Тип объявления',
    region: 'Регион',
    area: 'Площадь участка',
    price: 'Цена объявления',
    status: 'Статус',
    actions: 'Действия'
  };

  let myAdsEditingId = null;
  let myAdsSort = { column: null, direction: 'asc' };
  let myAdsWizardStep = 1;
  let myAdsNspdData = null;
  let myAdsPhotos = [];
  let myAdsManualMarker = null;
  let myAdsManualCoords = null;

  function getMyAdsColumns() {
    try {
      const saved = localStorage.getItem('myAdsColumnsOrder');
      if (saved) {
        const order = JSON.parse(saved);
        if (Array.isArray(order) && order.length === DEFAULT_COLUMNS.length && DEFAULT_COLUMNS.every(function(c) { return order.indexOf(c) !== -1; })) {
          return order;
        }
      }
    } catch (e) {}
    return DEFAULT_COLUMNS.slice();
  }
  function saveMyAdsColumns(order) {
    try { localStorage.setItem('myAdsColumnsOrder', JSON.stringify(order)); } catch (e) {}
  }

  function getMyAdsFilters() {
    const searchEl = document.getElementById('my-ads-filter-search');
    const statusEl = document.getElementById('my-ads-filter-status');
    const typeEl = document.getElementById('my-ads-filter-type');
    return {
      search: searchEl ? searchEl.value.trim().toLowerCase() : '',
      status: statusEl ? statusEl.value : '',
      type: typeEl ? typeEl.value : ''
    };
  }

  function filterMyAdsItems(items) {
    const filters = getMyAdsFilters();
    return items.filter(function(item) {
      const ad = item.ad_data || {};
      if (filters.status && item.status !== filters.status) return false;
      if (filters.type && ad.ad_type !== filters.type) return false;
      if (filters.search) {
        const q = filters.search;
        const region = (getRegionNameForAd(ad.region_code) || '').toLowerCase();
        const haystack = [
          (item.ad_id || '').toLowerCase(),
          (ad.cadastral_number || '').toLowerCase(),
          (ad.estate_address || '').toLowerCase(),
          region
        ].join(' ');
        if (haystack.indexOf(q) === -1) return false;
      }
      return true;
    });
  }

  function sortMyAdsItems(items) {
    if (!myAdsSort.column) return items;
    const col = myAdsSort.column;
    const dir = myAdsSort.direction === 'asc' ? 1 : -1;
    return items.slice().sort(function(a, b) {
      let va, vb;
      const adA = a.ad_data || {};
      const adB = b.ad_data || {};
      if (col === 'ad_id') { va = a.ad_id || ''; vb = b.ad_id || ''; }
      else if (col === 'cadastral_number') { va = adA.cadastral_number || ''; vb = adB.cadastral_number || ''; }
      else if (col === 'ad_type') { va = AD_TYPE_LABELS[adA.ad_type] || adA.ad_type || ''; vb = AD_TYPE_LABELS[adB.ad_type] || adB.ad_type || ''; }
      else if (col === 'region') { va = getRegionNameForAd(adA.region_code) || ''; vb = getRegionNameForAd(adB.region_code) || ''; }
      else if (col === 'area') { va = adA.area === undefined || adA.area === null ? -Infinity : Number(adA.area); vb = adB.area === undefined || adB.area === null ? -Infinity : Number(adB.area); }
      else if (col === 'price') {
        function adPrice(ad) {
          if (ad.ad_type === 'rent_own' || ad.ad_type === 'rent_sublet') return ad.rent_price_per_month;
          return ad.price;
        }
        va = adPrice(adA) === undefined || adPrice(adA) === null ? -Infinity : Number(adPrice(adA));
        vb = adPrice(adB) === undefined || adPrice(adB) === null ? -Infinity : Number(adPrice(adB));
      }
      else if (col === 'status') { va = AD_STATUS_LABELS[a.status] || a.status || ''; vb = AD_STATUS_LABELS[b.status] || b.status || ''; }
      else { return 0; }
      if (typeof va === 'number' && typeof vb === 'number') {
        return (va - vb) * dir;
      }
      va = String(va).toLowerCase();
      vb = String(vb).toLowerCase();
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }

  function getMyAdsCellHtml(columnId, item, index) {
    const ad = item.ad_data || {};
    if (columnId === 'num') return '<td>' + (index + 1) + '</td>';
    if (columnId === 'ad_id') return '<td class="mono">' + escapeHtml(item.ad_id || '') + '</td>';
    if (columnId === 'cadastral_number') return '<td class="mono">' + escapeHtml(ad.cadastral_number || '—') + '</td>';
    if (columnId === 'ad_type') return '<td>' + escapeHtml(AD_TYPE_LABELS[ad.ad_type] || ad.ad_type || '—') + '</td>';
    if (columnId === 'region') return '<td>' + escapeHtml(getRegionNameForAd(ad.region_code)) + '</td>';
    if (columnId === 'area') return '<td>' + escapeHtml(formatArea(ad.area)) + '</td>';
    if (columnId === 'price') {
      const displayPrice = (ad.ad_type === 'rent_own' || ad.ad_type === 'rent_sublet') ? ad.rent_price_per_month : ad.price;
      return '<td class="price">' + escapeHtml(formatPrice(displayPrice)) + '</td>';
    }
    if (columnId === 'status') {
      const statusLabel = AD_STATUS_LABELS[item.status] || item.status;
      const statusClass = AD_STATUS_CLASS[item.status] || 'inactive';
      return '<td><span class="my-ads-status ' + statusClass + '">' + escapeHtml(statusLabel) + '</span></td>';
    }
    if (columnId === 'actions') {
      const canEdit = item.status === 'pending' || item.status === 'rejected' || item.status === 'draft';
      const canArchive = item.status !== 'archived';
      const isArchived = item.status === 'archived';
      let html = '<td class="actions">';
      if (canEdit) {
        html += '<button class="action-btn my-ads-edit-btn" data-ad-id="' + escapeHtml(item.ad_id) + '" title="Редактировать"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>';
      }
      if (canArchive) {
        html += '<button class="action-btn my-ads-archive-btn" data-ad-id="' + escapeHtml(item.ad_id) + '" title="В архив"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg></button>';
      }
      if (isArchived) {
        html += '<button class="action-btn my-ads-restore-btn" data-ad-id="' + escapeHtml(item.ad_id) + '" title="Восстановить"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg></button>';
        html += '<button class="action-btn my-ads-delete-btn" data-ad-id="' + escapeHtml(item.ad_id) + '" title="Удалить"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>';
      }
      html += '</td>';
      return html;
    }
    return '<td></td>';
  }

  function renderMyAdsTable() {
    const wrap = document.getElementById('my-ads-table-wrap');
    if (!wrap) return;
    const items = window._myAdsData || [];
    const filtered = filterMyAdsItems(items);
    const sorted = sortMyAdsItems(filtered);
    const columns = getMyAdsColumns();

    if (sorted.length === 0) {
      wrap.innerHTML = '<div class="my-ads-empty"><div class="my-ads-empty-icon">🔍</div><p>Ничего не найдено</p></div>';
      return;
    }

    let html = '<table class="my-ads-table"><thead><tr>';
    columns.forEach(function(col) {
      const isSortable = col !== 'num' && col !== 'actions';
      const isSorted = myAdsSort.column === col;
      const arrow = isSorted ? (myAdsSort.direction === 'asc' ? '▲' : '▼') : '⇅';
      const cls = 'my-ads-th' + (isSortable ? ' my-ads-sort-header' : '') + (col === 'actions' ? ' actions' : '');
      html += '<th class="' + cls + '" data-column="' + col + '"' + (col !== 'actions' && col !== 'num' ? ' draggable="true"' : '') + '>';
      html += escapeHtml(COLUMN_TITLES[col] || col);
      if (isSortable) html += '<span class="my-ads-sort-arrow' + (isSorted ? ' active' : '') + '">' + arrow + '</span>';
      html += '</th>';
    });
    html += '</tr></thead><tbody>';
    sorted.forEach(function(item, idx) {
      html += '<tr>';
      columns.forEach(function(col) {
        html += getMyAdsCellHtml(col, item, idx);
      });
      html += '</tr>';
    });
    html += '</tbody></table>';
    wrap.innerHTML = html;

    // Sort handlers
    wrap.querySelectorAll('.my-ads-sort-header').forEach(function(th) {
      th.addEventListener('click', function() {
        const col = this.dataset.column;
        if (myAdsSort.column === col) {
          myAdsSort.direction = myAdsSort.direction === 'asc' ? 'desc' : 'asc';
        } else {
          myAdsSort.column = col;
          myAdsSort.direction = 'asc';
        }
        renderMyAdsTable();
      });
    });

    // Drag and drop handlers
    let dragSrc = null;
    wrap.querySelectorAll('th[draggable="true"]').forEach(function(th) {
      th.addEventListener('dragstart', function(e) {
        dragSrc = this;
        this.classList.add('dragging');
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', this.dataset.column);
      });
      th.addEventListener('dragenter', function(e) {
        if (this === dragSrc) return;
        this.classList.add('drag-over');
      });
      th.addEventListener('dragover', function(e) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      });
      th.addEventListener('dragleave', function() {
        this.classList.remove('drag-over');
      });
      th.addEventListener('drop', function(e) {
        e.preventDefault();
        if (this === dragSrc) return;
        const fromCol = e.dataTransfer.getData('text/plain');
        const toCol = this.dataset.column;
        const order = getMyAdsColumns();
        const fromIdx = order.indexOf(fromCol);
        const toIdx = order.indexOf(toCol);
        if (fromIdx !== -1 && toIdx !== -1) {
          order.splice(fromIdx, 1);
          order.splice(toIdx, 0, fromCol);
          saveMyAdsColumns(order);
        }
        renderMyAdsTable();
      });
      th.addEventListener('dragend', function() {
        this.classList.remove('dragging');
        wrap.querySelectorAll('th').forEach(function(t) { t.classList.remove('drag-over'); });
        dragSrc = null;
      });
    });

    // Edit/Archive handlers
    wrap.querySelectorAll('.my-ads-edit-btn').forEach(function(btn) {
      btn.addEventListener('click', async function() {
        try {
          const detailRes = await apiFetch('/api/user/ads/' + encodeURIComponent(btn.dataset.adId));
          if (!detailRes.ok) throw new Error('HTTP ' + detailRes.status);
          const detail = await detailRes.json();
          window.openMyAdsForm(detail.ad);
        } catch (e) {
          alert('Ошибка загрузки объявления: ' + e.message);
        }
      });
    });
    wrap.querySelectorAll('.my-ads-archive-btn').forEach(function(btn) {
      btn.addEventListener('click', async function() {
        try {
          const delRes = await apiFetch('/api/user/ads/' + encodeURIComponent(btn.dataset.adId), { method: 'DELETE' });
          if (!delRes.ok) throw new Error('HTTP ' + delRes.status);
          if (window.swrInvalidate) window.swrInvalidate('myads');
          window.renderMyAdsPanel();
        } catch (e) {
          alert('Ошибка архивирования: ' + e.message);
        }
      });
    });
    wrap.querySelectorAll('.my-ads-restore-btn').forEach(function(btn) {
      btn.addEventListener('click', async function() {
        try {
          const res = await apiFetch('/api/user/ads/' + encodeURIComponent(btn.dataset.adId) + '/restore', { method: 'POST' });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          if (window.swrInvalidate) window.swrInvalidate('myads');
          window.renderMyAdsPanel();
        } catch (e) {
          alert('Ошибка восстановления: ' + e.message);
        }
      });
    });
    wrap.querySelectorAll('.my-ads-delete-btn').forEach(function(btn) {
      btn.addEventListener('click', async function() {
        if (!confirm('Удалить объявление безвозвратно?')) return;
        try {
          const res = await apiFetch('/api/user/ads/' + encodeURIComponent(btn.dataset.adId) + '/permanent', { method: 'DELETE' });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          if (window.swrInvalidate) window.swrInvalidate('myads');
          window.renderMyAdsPanel();
        } catch (e) {
          alert('Ошибка удаления: ' + e.message);
        }
      });
    });
  }

  function applyMyAds(data) {
    window._myAdsData = data.items || [];
    const wrap = document.getElementById('my-ads-table-wrap');
    if (!wrap) return;
    if (window._myAdsData.length === 0) {
      wrap.innerHTML = '<div class="my-ads-empty"><div class="my-ads-empty-icon">📋</div><p>У вас пока нет объявлений</p></div>';
      return;
    }
    renderMyAdsTable();
  }

  window.renderMyAdsPanel = async function() {
    const wrap = document.getElementById('my-ads-table-wrap');
    if (!wrap) return;
    // A4: лоадер только если нет кешированного снимка; иначе мгновенный рендер
    if (!window.swrPeek('myads')) {
      wrap.innerHTML = '<div style="padding:8px 0"><div class="skel-row"></div><div class="skel-row w60"></div><div class="skel-row"></div><div class="skel-row w40"></div></div>';
    }
    try {
      const res = await window.swrFetch('myads', '/api/user/ads', {}, {
        maxAgeMs: 30000,
        onRefresh: function(fresh) { applyMyAds(fresh); }
      });
      applyMyAds(res.data);
    } catch (e) {
      if (e.status === 401) {
        wrap.innerHTML = '<div class="my-ads-empty"><div class="my-ads-empty-icon">🔒</div><p>Войдите, чтобы увидеть свои объявления</p></div>';
        return;
      }
      wrap.innerHTML = '<div class="my-ads-empty" style="color:#e74c3c;"><div class="my-ads-empty-icon">⚠️</div><p>Ошибка загрузки: ' + escapeHtml(e.message) + '</p></div>';
    }
  };

  let myAdsFormMap = null;
  let myAdsFormMapReady = false;

  function isValidCadastralInput(value) {
    return /^\d+:\d+:\d+:\d+$/.test((value || '').trim());
  }

  function setFormValue(id, value) {
    const el = document.getElementById(id);
    if (!el) return;
    el.value = value !== undefined && value !== null ? value : '';
  }

  let myAdsFormErrorTimer = null;

  function showFormError(msg) {
    const errorEl = document.getElementById('my-ads-form-error');
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
    if (myAdsFormErrorTimer) clearTimeout(myAdsFormErrorTimer);
    myAdsFormErrorTimer = setTimeout(function() {
      hideFormError();
    }, 2000);
  }

  function hideFormError() {
    const errorEl = document.getElementById('my-ads-form-error');
    if (errorEl) errorEl.style.display = 'none';
    if (myAdsFormErrorTimer) {
      clearTimeout(myAdsFormErrorTimer);
      myAdsFormErrorTimer = null;
    }
  }

  function showWizardLoader(show) {
    const loader = document.getElementById('my-ads-wizard-loader');
    if (!loader) return;
    loader.style.display = show ? 'flex' : 'none';
  }

  function goToMyAdsStep(step) {
    myAdsWizardStep = step;
    for (let i = 1; i <= 2; i++) {
      const el = document.getElementById('my-ads-step-' + i);
      if (el) el.classList.toggle('hidden', step !== i);
    }
    const step3 = document.getElementById('my-ads-step-3');
    if (step3) step3.classList.add('hidden');
    hideFormError();
    if (step === 2) {
      // Map container may have been hidden; trigger resize after it becomes visible
      setTimeout(function() {
        if (myAdsFormMap) {
          myAdsFormMap.resize();
          if (!myAdsNspdData || !myAdsNspdData.coordinates) {
            myAdsFormMap.flyTo({ center: [37.6, 55.7], zoom: 4, duration: 0 });
          }
        }
      }, 100);
    }
  }

  // ── Coordinate reprojection (EPSG:3857 meters → EPSG:4326 WGS84 degrees)
  function reproject3857To4326(x, y) {
    const R = 6378137;
    const lon = (x / R) * (180 / Math.PI);
    const lat = (180 / Math.PI) * (2 * Math.atan(Math.exp(y / R)) - Math.PI / 2);
    return [lon, lat];
  }

  function reprojectRing(ring) {
    return ring.map(function(c) { return reproject3857To4326(c[0], c[1]); });
  }

  function reprojectPolygonCoords(coords) {
    return coords.map(reprojectRing);
  }

  function isProjectedCoords(coords) {
    return coords && coords[0] && Array.isArray(coords[0][0]) && Math.abs(coords[0][0][0]) > 180;
  }

  function getPolygonBounds(coords) {
    let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
    coords.forEach(function(ring) {
      ring.forEach(function(c) {
        const lon = c[0], lat = c[1];
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      });
    });
    return [minLon, minLat, maxLon, maxLat];
  }

  function initMyAdsFormMap() {
    const container = document.getElementById('my-ads-form-map');
    if (!container || !window.maplibregl) return;
    if (myAdsFormMap) {
      setTimeout(function() { myAdsFormMap.resize(); }, 50);
      updateWizardPolygon();
      return;
    }

    const style = {
      version: 8,
      sources: {
        'wizard-osm': {
          type: 'raster',
          tiles: [window.location.origin + '/tiles/proxy/osm/{z}/{x}/{y}.png'],
          tileSize: 256,
          maxzoom: 19,
          attribution: '© OSM'
        }
      },
      layers: [{
        id: 'wizard-osm',
        type: 'raster',
        source: 'wizard-osm',
        paint: { 'raster-fade-duration': 0 }
      }]
    };

    const map = new window.maplibregl.Map({
      container: container,
      style: style,
      center: [37.6, 55.7],
      zoom: 4,
      minZoom: 2,
      maxZoom: 19,
      attributionControl: false,
      projection: { type: 'mercator' },
      dragRotate: false,
      touchPitch: false
    });
    myAdsFormMap = map;

    map.on('load', function() {
      map.addSource('wizard-polygon', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });
      map.addLayer({
        id: 'wizard-polygon-fill',
        type: 'fill',
        source: 'wizard-polygon',
        paint: {
          'fill-color': 'rgba(102, 126, 234, 0.25)',
          'fill-outline-color': '#667eea'
        }
      });
      map.addLayer({
        id: 'wizard-polygon-line',
        type: 'line',
        source: 'wizard-polygon',
        paint: {
          'line-color': '#667eea',
          'line-width': 3
        }
      });
      map.on('click', function(e) {
        if (!myAdsNspdData || !myAdsNspdData.coordinates) {
          setManualMarker(e.lngLat.lng, e.lngLat.lat);
        }
      });
      myAdsFormMapReady = true;
      updateWizardPolygon();
      if (myAdsManualCoords) {
        setManualMarker(myAdsManualCoords.lng, myAdsManualCoords.lat);
      }
    });
  }

  function setManualMarker(lng, lat) {
    if (!myAdsFormMap) return;
    myAdsManualCoords = { lng: lng, lat: lat };
    if (myAdsManualMarker) {
      myAdsManualMarker.setLngLat([lng, lat]);
    } else {
      const el = document.createElement('div');
      el.className = 'my-ads-map-marker';
      myAdsManualMarker = new window.maplibregl.Marker({ element: el, draggable: true })
        .setLngLat([lng, lat])
        .addTo(myAdsFormMap);
      myAdsManualMarker.on('dragend', function() {
        const ll = myAdsManualMarker.getLngLat();
        myAdsManualCoords = { lng: ll.lng, lat: ll.lat };
      });
    }
  }

  function updateWizardPolygon() {
    const overlay = document.getElementById('my-ads-form-map-overlay');
    if (!myAdsFormMap || !myAdsFormMapReady) return;

    const source = myAdsFormMap.getSource('wizard-polygon');
    if (!source) return;

    if (!myAdsNspdData || !myAdsNspdData.coordinates) {
      source.setData({ type: 'FeatureCollection', features: [] });
      if (overlay) {
        overlay.textContent = myAdsManualCoords ? '' : 'Кликните по карте, чтобы указать расположение земельного участка';
        overlay.style.display = myAdsManualCoords ? 'none' : 'flex';
      }
      return;
    }

    let geom = myAdsNspdData.coordinates;
    if (typeof geom === 'string') {
      try { geom = JSON.parse(geom); } catch (e) { geom = null; }
    }
    if (!geom || !geom.coordinates) {
      source.setData({ type: 'FeatureCollection', features: [] });
      if (overlay) overlay.style.display = 'flex';
      return;
    }

    if (overlay) overlay.style.display = 'none';

    const coords = isProjectedCoords(geom.coordinates)
      ? reprojectPolygonCoords(geom.coordinates)
      : geom.coordinates;

    source.setData({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: coords },
        properties: {}
      }]
    });

    const bounds = getPolygonBounds(coords);
    if (bounds[0] !== Infinity) {
      myAdsFormMap.fitBounds(bounds, { padding: 40, duration: 0, maxZoom: 18 });
    }
  }

  function destroyMyAdsFormMap() {
    if (myAdsFormMap) {
      myAdsFormMap.remove();
      myAdsFormMap = null;
      myAdsFormMapReady = false;
    }
  }

  function renderNspdSummary() {
    const summaryEl = document.getElementById('my-ads-nspd-summary');
    if (!summaryEl) return;
    const d = myAdsNspdData || {};
    const hasData = d.cadastral_number || d.estate_address || d.area != null || d.land_category || d.permitted_use || d.region_code;
    if (!hasData) {
      summaryEl.innerHTML = '<div class="my-ads-nspd-row"><span>Кадастровый номер не указан. Продолжите без привязки участка.</span></div>';
      return;
    }
    let html = '';
    if (d.cadastral_number) html += '<div class="my-ads-nspd-row"><span>Кадастровый номер:</span><span>' + escapeHtml(d.cadastral_number) + '</span></div>';
    if (d.area != null) html += '<div class="my-ads-nspd-row"><span>Площадь:</span><span>' + Number(d.area).toLocaleString('ru-RU') + ' м²</span></div>';
    if (d.permitted_use) html += '<div class="my-ads-nspd-row"><span>ВРИ:</span><span>' + escapeHtml(d.permitted_use) + '</span></div>';
    else if (d.land_category) html += '<div class="my-ads-nspd-row"><span>Категория:</span><span>' + escapeHtml(d.land_category) + '</span></div>';
    if (d.estate_address) html += '<div class="my-ads-nspd-row"><span>Адрес:</span><span>' + escapeHtml(d.estate_address) + '</span></div>';
    const regionName = d.region_code && typeof window.getRegionName === 'function' ? window.getRegionName(d.region_code) : d.region_code;
    if (regionName) html += '<div class="my-ads-nspd-row"><span>Регион:</span><span>' + escapeHtml(regionName) + '</span></div>';
    summaryEl.innerHTML = html;
  }

  function renderStep2() {
    renderNspdSummary();
    renderStep3Fields();
    setTimeout(function() { initMyAdsFormMap(); }, 0);
  }

  function validateStep1() {
    const typeEl = document.getElementById('my-ads-ad-type-step1');
    const input = document.getElementById('my-ads-wizard-cadastral');
    const btn = document.getElementById('my-ads-next-1');
    if (btn) {
      const typeSelected = typeEl && typeEl.value;
      const cadastralValid = input ? isValidCadastralInput(input.value) : false;
      btn.disabled = !(typeSelected && cadastralValid);
    }
    hideDuplicateCadastralMessage();
  }

  function showDuplicateCadastralMessage(cn, owner) {
    const box = document.getElementById('my-ads-cadastral-duplicate');
    if (!box) return;
    box.innerHTML = 'Кадастровый номер <strong>' + escapeHtml(cn) + '</strong> используется пользователем: ' + escapeHtml(owner) + '.<br>Создание объявления с этим кадастровым номером невозможно.';
    box.style.display = 'block';
  }

  function hideDuplicateCadastralMessage() {
    const box = document.getElementById('my-ads-cadastral-duplicate');
    if (box) box.style.display = 'none';
  }

  async function checkCadastralInNspd() {
    const input = document.getElementById('my-ads-wizard-cadastral');
    if (!input) return;
    const cn = input.value.trim();
    if (!isValidCadastralInput(cn)) return;

    hideFormError();
    hideDuplicateCadastralMessage();
    showWizardLoader(true);

    try {
      const dupRes = await apiFetch('/api/user/ads/check-cadastral?cadastral_number=' + encodeURIComponent(cn));
      const dupData = await dupRes.json().catch(function() { return {}; });
      if (dupRes.ok && dupData.used) {
        showDuplicateCadastralMessage(cn, dupData.owner || 'другой пользователь');
        return;
      }
      const res = await apiFetch('/api/nspd/lookup?cadastral_number=' + encodeURIComponent(cn));
      const data = await res.json().catch(function() { return {}; });
      if (!res.ok) {
        throw new Error(data.error || 'Не удалось получить данные по кадастровому номеру');
      }
      myAdsNspdData = data.data || null;
      setFormValue('my-ads-cadastral', cn.replace(/\s+/g, ''));
      setFormValue('my-ads-area', myAdsNspdData && myAdsNspdData.area != null ? myAdsNspdData.area : '');
      setFormValue('my-ads-address-hidden', myAdsNspdData && myAdsNspdData.estate_address ? myAdsNspdData.estate_address : '');
      setFormValue('my-ads-region-hidden', myAdsNspdData && myAdsNspdData.region_code ? myAdsNspdData.region_code : '');
      renderStep2();
      goToMyAdsStep(2);
    } catch (e) {
      showFormError(e.message);
    } finally {
      showWizardLoader(false);
    }
  }

  function showNoCadastralOptions() {
    const box = document.getElementById('my-ads-no-cadastral-options');
    if (box) box.classList.add('visible');
  }

  function hideNoCadastralOptions() {
    const box = document.getElementById('my-ads-no-cadastral-options');
    if (box) box.classList.remove('visible');
  }

  function proceedWithoutCadastral() {
    hideNoCadastralOptions();
    myAdsNspdData = null;
    myAdsManualCoords = null;
    if (myAdsManualMarker) {
      myAdsManualMarker.remove();
      myAdsManualMarker = null;
    }
    setFormValue('my-ads-wizard-cadastral', '');
    setFormValue('my-ads-cadastral', '');
    setFormValue('my-ads-area', '');
    setFormValue('my-ads-address-hidden', '');
    setFormValue('my-ads-region-hidden', '');
    renderStep2();
    goToMyAdsStep(2);
  }

  function renderStep3Fields() {
    const type = document.getElementById('my-ads-ad-type-step1').value;
    document.getElementById('my-ads-fields-sale_ownership').classList.toggle('hidden', type !== 'sale_ownership');
    document.getElementById('my-ads-fields-sale_lease_right').classList.toggle('hidden', type !== 'sale_lease_right');
    document.getElementById('my-ads-fields-rent').classList.toggle('hidden', type !== 'rent_own' && type !== 'rent_sublet');

    const submitBtn = document.getElementById('my-ads-submit-btn');
    if (submitBtn) submitBtn.textContent = myAdsEditingId ? 'Сохранить и отправить на модерацию' : 'Отправить на модерацию';
  }

  function renderPhotoPreview() {
    const wrap = document.getElementById('my-ads-photo-preview');
    if (!wrap) return;
    if (myAdsPhotos.length === 0) {
      wrap.innerHTML = '';
      return;
    }
    let html = '';
    myAdsPhotos.forEach(function(url, idx) {
      const isUploading = url === '_uploading_';
      const thumbUrl = isUploading ? '' : (url + (url.indexOf('?') === -1 ? '?w=200' : '&w=200'));
      html += '<div class="my-ads-photo-thumb' + (isUploading ? ' my-ads-photo-uploading' : '') + '" data-idx="' + idx + '">';
      if (!isUploading) html += '<img src="' + escapeHtml(thumbUrl) + '" alt="">';
      html += '<button class="my-ads-photo-remove" type="button" title="Удалить">×</button>';
      html += '</div>';
    });
    wrap.innerHTML = html;

    wrap.querySelectorAll('.my-ads-photo-remove').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        const idx = parseInt(this.parentElement.dataset.idx, 10);
        if (!isNaN(idx)) removePhoto(idx);
      });
    });
  }

  function removePhoto(idx) {
    myAdsPhotos.splice(idx, 1);
    renderPhotoPreview();
  }

  async function uploadPhoto(file) {
    const token = (window.goszemlyaAuth && typeof window.goszemlyaAuth.getToken === 'function') ? window.goszemlyaAuth.getToken() : '';
    const uploadingIdx = myAdsPhotos.length;
    myAdsPhotos.push('_uploading_');
    renderPhotoPreview();

    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await fetch('/api/user/ads/upload-photo', {
        method: 'POST',
        headers: token ? { 'Authorization': 'Bearer ' + token } : {},
        body: formData,
      });
      const data = await res.json().catch(function() { return {}; });
      if (!res.ok || !data.url) {
        throw new Error(data.error || 'Ошибка загрузки фото');
      }
      myAdsPhotos[uploadingIdx] = data.url;
    } catch (e) {
      myAdsPhotos.splice(uploadingIdx, 1);
      showFormError('Не удалось загрузить фото: ' + e.message);
    }
    renderPhotoPreview();
  }

  function handlePhotoInput(files) {
    if (!files || files.length === 0) return;
    Array.from(files).forEach(function(file) {
      if (!/^image\//.test(file.type)) return;
      uploadPhoto(file);
    });
    const input = document.getElementById('my-ads-photo-input');
    if (input) input.value = '';
  }

  function collectFormPayload(status) {
    const adType = document.getElementById('my-ads-ad-type-step1').value;
    const payload = {
      ad_type: adType,
      cadastral_number: document.getElementById('my-ads-cadastral').value.trim() || null,
      area: document.getElementById('my-ads-area').value ? Number(document.getElementById('my-ads-area').value) : null,
      estate_address: document.getElementById('my-ads-address-hidden').value.trim() || null,
      region_code: document.getElementById('my-ads-region-hidden').value.trim() || null,
      status: status,
    };

    if (adType === 'sale_ownership') {
      payload.price = document.getElementById('my-ads-sale-price').value ? Number(document.getElementById('my-ads-sale-price').value) : null;
      payload.description = document.getElementById('my-ads-sale-description').value.trim() || null;
    } else if (adType === 'sale_lease_right') {
      payload.lease_until_year = document.getElementById('my-ads-lease-until-year').value ? Number(document.getElementById('my-ads-lease-until-year').value) : null;
      payload.price = document.getElementById('my-ads-lease-right-price').value ? Number(document.getElementById('my-ads-lease-right-price').value) : null;
      payload.description = document.getElementById('my-ads-lease-description').value.trim() || null;
    } else {
      payload.rent_price_per_month = document.getElementById('my-ads-rent-price').value ? Number(document.getElementById('my-ads-rent-price').value) : null;
      payload.description = document.getElementById('my-ads-rent-description').value.trim() || null;
    }
    payload.photos = myAdsPhotos.filter(function(u) { return u && u !== '_uploading_'; });
    if (myAdsNspdData && myAdsNspdData.coordinates) {
      payload.coordinates = myAdsNspdData.coordinates;
    }
    if (myAdsManualCoords) {
      payload.manual_coords = myAdsManualCoords;
    }
    return payload;
  }

  function showSubmitSuccess(adId) {
    const modal = document.getElementById('my-ads-success-modal');
    const idEl = document.getElementById('my-ads-success-ad-id');
    if (idEl) idEl.textContent = '№' + escapeHtml(adId);
    if (modal) modal.classList.add('visible');
  }

  function hideSubmitSuccess() {
    const modal = document.getElementById('my-ads-success-modal');
    if (modal) modal.classList.remove('visible');
  }

  function getAdPriceValue() {
    const adType = document.getElementById('my-ads-ad-type-step1').value;
    if (adType === 'rent_own' || adType === 'rent_sublet') {
      return document.getElementById('my-ads-rent-price').value;
    }
    if (adType === 'sale_lease_right') {
      return document.getElementById('my-ads-lease-right-price').value;
    }
    return document.getElementById('my-ads-sale-price').value;
  }

  async function submitMyAdsForm(status) {
    const errorEl = document.getElementById('my-ads-form-error');
    const payload = collectFormPayload(status);
    if (!payload.ad_type) {
      showFormError('Укажите тип объявления');
      return;
    }

    // Price is required for moderation (both new and edited ads)
    if (status === 'pending') {
      const priceValue = getAdPriceValue();
      if (!priceValue || Number(priceValue) <= 0) {
        showFormError('Укажите цену для публикации объявления');
        return;
      }
    }

    try {
      let res;
      if (myAdsEditingId) {
        res = await apiFetch('/api/user/ads/' + encodeURIComponent(myAdsEditingId), { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        res = await apiFetch('/api/user/ads', { method: 'POST', body: JSON.stringify(payload) });
      }
      const data = await res.json().catch(function() { return {}; });
      if (!res.ok) {
        showFormError(data.error || 'Ошибка сохранения объявления');
        return;
      }
      if (status === 'pending') {
        const adId = data.ad && data.ad.ad_id ? data.ad.ad_id : (data.ad ? data.ad.ad_id : '');
        showSubmitSuccess(adId);
      } else {
        window.closeMyAdsForm();
        if (window.swrInvalidate) window.swrInvalidate('myads');
        if (typeof window.renderMyAdsPanel === 'function') window.renderMyAdsPanel();
      }
    } catch (e) {
      showFormError('Ошибка сети: ' + e.message);
    }
  }

  function hasUnsavedMyAdsData() {
    if (myAdsEditingId) return true;
    if (myAdsWizardStep > 1) return true;
    const cn = document.getElementById('my-ads-wizard-cadastral');
    if (cn && cn.value.trim()) return true;
    return false;
  }

  function hideExitConfirm() {
    const box = document.getElementById('my-ads-exit-confirm');
    if (box) box.classList.remove('visible');
  }

  window.confirmCloseMyAdsForm = function() {
    if (!hasUnsavedMyAdsData()) {
      window.closeMyAdsForm();
      return;
    }
    const box = document.getElementById('my-ads-exit-confirm');
    if (box) box.classList.add('visible');
  };

  window.openMyAdsForm = function(ad) {
    const modal = document.getElementById('my-ads-form-modal');
    if (!modal) return;
    myAdsEditingId = ad ? ad.ad_id : null;
    myAdsNspdData = null;
    hideFormError();
    hideDuplicateCadastralMessage();
    hideExitConfirm();

    const adData = ad ? (ad.ad_data || ad) : {};
    setFormValue('my-ads-ad-type-step1', adData.ad_type || '');

    if (myAdsEditingId) {
      // Editing: jump to step 2 with existing data
      setFormValue('my-ads-cadastral', adData.cadastral_number || '');
      setFormValue('my-ads-area', adData.area !== undefined && adData.area !== null ? adData.area : '');
      setFormValue('my-ads-address-hidden', adData.estate_address || '');
      setFormValue('my-ads-region-hidden', adData.region_code || '');
      myAdsNspdData = {
        cadastral_number: adData.cadastral_number || null,
        area: adData.area || null,
        estate_address: adData.estate_address || null,
        region_code: adData.region_code || null,
        coordinates: adData.coordinates || null
      };

      myAdsManualCoords = adData.manual_coords || null;
      if (!myAdsManualMarker && myAdsManualCoords) {
        // marker will be added when map loads
      }

      setFormValue('my-ads-sale-price', '');
      setFormValue('my-ads-sale-description', '');
      setFormValue('my-ads-lease-until-year', '');
      setFormValue('my-ads-lease-right-price', '');
      setFormValue('my-ads-lease-description', '');
      setFormValue('my-ads-rent-price', '');
      setFormValue('my-ads-rent-description', '');

      if (adData.ad_type === 'sale_ownership') {
        setFormValue('my-ads-sale-price', adData.price !== undefined && adData.price !== null ? adData.price : '');
        setFormValue('my-ads-sale-description', adData.description || '');
      } else if (adData.ad_type === 'sale_lease_right') {
        setFormValue('my-ads-lease-until-year', adData.lease_until_year || '');
        setFormValue('my-ads-lease-right-price', adData.price !== undefined && adData.price !== null ? adData.price : '');
        setFormValue('my-ads-lease-description', adData.description || '');
      } else {
        setFormValue('my-ads-rent-price', adData.rent_price_per_month !== undefined && adData.rent_price_per_month !== null ? adData.rent_price_per_month : '');
        setFormValue('my-ads-rent-description', adData.description || '');
      }

      myAdsPhotos = Array.isArray(adData.photos) ? adData.photos.slice() : [];
      renderPhotoPreview();
      renderStep2();
      goToMyAdsStep(2);
    } else {
      // Creating: start from step 1
      myAdsManualCoords = null;
      if (myAdsManualMarker) { myAdsManualMarker.remove(); myAdsManualMarker = null; }
      setFormValue('my-ads-wizard-cadastral', '');
      setFormValue('my-ads-cadastral', '');
      setFormValue('my-ads-area', '');
      setFormValue('my-ads-address-hidden', '');
      setFormValue('my-ads-region-hidden', '');
      setFormValue('my-ads-sale-price', '');
      setFormValue('my-ads-sale-description', '');
      setFormValue('my-ads-lease-until-year', '');
      setFormValue('my-ads-lease-right-price', '');
      setFormValue('my-ads-lease-description', '');
      setFormValue('my-ads-rent-price', '');
      setFormValue('my-ads-rent-description', '');
      myAdsPhotos = [];
      renderPhotoPreview();
      validateStep1();
      goToMyAdsStep(1);
    }

    modal.style.display = 'flex';
  };

  window.closeMyAdsForm = function() {
    const modal = document.getElementById('my-ads-form-modal');
    if (!modal) return;
    modal.style.display = 'none';
    myAdsEditingId = null;
    myAdsNspdData = null;
    myAdsWizardStep = 1;
    myAdsPhotos = [];
    myAdsManualCoords = null;
    renderPhotoPreview();
    destroyMyAdsFormMap();
    hideFormError();
    hideExitConfirm();
    hideNoCadastralOptions();
    hideSubmitSuccess();
    const ids = [
      'my-ads-wizard-cadastral', 'my-ads-cadastral', 'my-ads-area',
      'my-ads-address-hidden', 'my-ads-region-hidden',
      'my-ads-sale-price', 'my-ads-sale-description',
      'my-ads-lease-until-year', 'my-ads-lease-right-price', 'my-ads-lease-description',
      'my-ads-rent-price', 'my-ads-rent-description'
    ];
    ids.forEach(function(id) { setFormValue(id, ''); });
    const typeEl = document.getElementById('my-ads-ad-type-step1');
    if (typeEl) typeEl.selectedIndex = 0;
    goToMyAdsStep(1);
  };

  function initMyAdsForm() {
    const createBtn = document.getElementById('my-ads-create-btn');
    if (createBtn) {
      createBtn.addEventListener('click', function(e) {
        e.stopPropagation();
        const auth = window.goszemlyaAuth;
        if (auth && typeof auth.requireAuth === 'function') {
          if (!auth.requireAuth()) return;
        }
        window.openMyAdsForm(null);
      });
    }

    const cadastralInput = document.getElementById('my-ads-wizard-cadastral');
    if (cadastralInput) {
      cadastralInput.addEventListener('input', validateStep1);
      cadastralInput.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          checkCadastralInNspd();
        }
      });
    }

    const typeEl = document.getElementById('my-ads-ad-type-step1');
    if (typeEl) typeEl.addEventListener('change', validateStep1);

    const next1 = document.getElementById('my-ads-next-1');
    if (next1) next1.addEventListener('click', checkCadastralInNspd);

    const noCadastralBtn = document.getElementById('my-ads-no-cadastral');
    if (noCadastralBtn) noCadastralBtn.addEventListener('click', function(e) {
      e.preventDefault();
      showNoCadastralOptions();
    });

    const getCadastralBtn = document.getElementById('my-ads-get-cadastral-btn');
    if (getCadastralBtn) getCadastralBtn.addEventListener('click', function() {
      hideNoCadastralOptions();
      window.open('https://pkk.rosreestr.ru/', '_blank', 'noopener,noreferrer');
    });

    const proceedNoCadastralBtn = document.getElementById('my-ads-proceed-no-cadastral');
    if (proceedNoCadastralBtn) proceedNoCadastralBtn.addEventListener('click', function(e) {
      e.preventDefault();
      proceedWithoutCadastral();
    });

    const noCadastralBack = document.getElementById('my-ads-no-cadastral-back');
    if (noCadastralBack) noCadastralBack.addEventListener('click', hideNoCadastralOptions);

    const noCadastralClose = document.getElementById('my-ads-no-cadastral-close');
    if (noCadastralClose) noCadastralClose.addEventListener('click', hideNoCadastralOptions);

    const back2 = document.getElementById('my-ads-back-2');
    if (back2) back2.addEventListener('click', function() { goToMyAdsStep(1); });

    const photoInput = document.getElementById('my-ads-photo-input');
    if (photoInput) {
      photoInput.addEventListener('change', function() {
        handlePhotoInput(this.files);
      });
    }

    const photoAddBtn = document.getElementById('my-ads-photo-add-btn');
    if (photoAddBtn) {
      photoAddBtn.addEventListener('click', function(e) {
        const input = document.getElementById('my-ads-photo-input');
        if (input) input.click();
      });
    }

    const submitBtn = document.getElementById('my-ads-submit-btn');
    if (submitBtn) submitBtn.addEventListener('click', function() { submitMyAdsForm('pending'); });

    const saveDraftBtn = document.getElementById('my-ads-save-draft-btn');
    if (saveDraftBtn) saveDraftBtn.addEventListener('click', function() { submitMyAdsForm('draft'); });

    const discardBtn = document.getElementById('my-ads-discard-btn');
    if (discardBtn) discardBtn.addEventListener('click', window.closeMyAdsForm);

    const cancelExitBtn = document.getElementById('my-ads-cancel-exit-btn');
    if (cancelExitBtn) cancelExitBtn.addEventListener('click', hideExitConfirm);

    const successClose = document.getElementById('my-ads-success-close');
    if (successClose) successClose.addEventListener('click', onSubmitSuccessClose);

    const successOk = document.getElementById('my-ads-success-ok');
    if (successOk) successOk.addEventListener('click', onSubmitSuccessClose);
  }

  function onSubmitSuccessClose() {
    hideSubmitSuccess();
    window.closeMyAdsForm();
    if (window.swrInvalidate) window.swrInvalidate('myads');
    if (typeof window.renderMyAdsPanel === 'function') window.renderMyAdsPanel();
  }

  // ── Remember source page for /obyavleniya close button ─────────
  function initMyAdsFilters() {
    const searchEl = document.getElementById('my-ads-filter-search');
    const statusEl = document.getElementById('my-ads-filter-status');
    const typeEl = document.getElementById('my-ads-filter-type');
    const resetEl = document.getElementById('my-ads-filter-reset');

    function onFilterChange() {
      myAdsSort.column = null;
      myAdsSort.direction = 'asc';
      if (typeof window._myAdsData !== 'undefined' && window._myAdsData.length > 0) {
        renderMyAdsTable();
      }
    }

    if (searchEl) searchEl.addEventListener('input', onFilterChange);
    if (statusEl) statusEl.addEventListener('change', onFilterChange);
    if (typeEl) typeEl.addEventListener('change', onFilterChange);

    if (resetEl) {
      resetEl.addEventListener('click', function() {
        if (searchEl) searchEl.value = '';
        if (statusEl) statusEl.value = '';
        if (typeEl) typeEl.value = '';
        onFilterChange();
      });
    }
  }

  function initListingsBackLink() {
    document.querySelectorAll('.menu-item[href="/obyavleniya"]').forEach(function(link) {
      link.addEventListener('click', function(e) {
        try {
          sessionStorage.setItem('listingsBackUrl', window.location.href);
        } catch (err) {}
      });
    });
  }

  // ── Initialize everything ──────────────────────────────────────
  function initUI() {
    initPanelHandlers();
    initCadastreControls();
    initStateManagement();
    initVRI();
    initAutoOpenLot();
    initListingsBackLink();
    initMyAdsForm();
    initMyAdsFilters();
    // Ensure our favorites renderer wins over any earlier definition
    window.renderFavoritesPanel = renderFavoritesPanel;
    log('INIT', 'UI Core initialized successfully');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUI);
  } else {
    initUI();
  }
})();
